/**
 * Organizations hook: list + create + switch active org.
 *
 * `setActive` is the subtle one. Switching org re-issues the `access_token`
 * cookie server-side, but the new `o` claim only reaches the app after a token
 * refresh. So the mutation runs: set-active → `refresh()` (pull the new JWT) →
 * invalidate `qk.me()` so `/me` (and `active_org`) refetch. Without the refresh,
 * the UI would keep the old org context until the next proactive refresh.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { refresh } from '../lib/endpoints/auth'
import {
  clearActiveOrganization,
  createOrganization,
  listOrganizations,
  setActiveOrganization,
} from '../lib/endpoints/organizations'
import { qk } from '../lib/queryKeys'
import type { Organization } from '../lib/schemas'

export function useOrganizations(enabled = true) {
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: qk.organizations(),
    queryFn: listOrganizations,
    enabled,
  })

  const create = useMutation<
    Organization,
    Error,
    { name: string; slug: string }
  >({
    mutationFn: ({ name, slug }) => createOrganization(name, slug),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.organizations() }),
  })

  const setActive = useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await setActiveOrganization(id)
      // Pull the fresh JWT (now carrying `o`) before anything reads /me.
      await refresh()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me() }),
  })

  // Switch back to personal context. Same set-active → refresh → invalidate flow.
  const clearActive = useMutation<void, Error, void>({
    mutationFn: async () => {
      await clearActiveOrganization()
      await refresh()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me() }),
  })

  return { query, create, setActive, clearActive }
}
